import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";

const usePermissions = ({ BASE_URL, profile }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [modulePermissions, setModulePermissions] = useState({
    view: true,
    edit: true,
    detectCompanies: true,
    autoCreateConfigs: true,
    manageDepartmentRules: true,
    reset: true,
    viewCompanyInfo: true,
    editCompanyInfo: true,
    viewAttendance: true,
    editAttendance: true,
    viewOvertime: true,
    editOvertime: true,
    viewLeaves: true,
    editLeaves: true,
    viewAdvancements: true,
    editAdvancements: true,
  });
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  useEffect(() => {
    const fetchPermissions = async () => {
      const fullAdminPermissions = {
        view: true,
        edit: true,
        detectCompanies: true,
        autoCreateConfigs: true,
        manageDepartmentRules: true,
        reset: true,
        viewCompanyInfo: true,
        editCompanyInfo: true,
        viewAttendance: true,
        editAttendance: true,
        viewOvertime: true,
        editOvertime: true,
        viewLeaves: true,
        editLeaves: true,
        viewAdvancements: true,
        editAdvancements: true,
      };

      if (profile?.role === "admin" || profile?.role === "superadmin") {
        console.log("🔐 Admin user detected via profile - bypassing all permission checks");
        setIsAdmin(true);
        setModulePermissions(fullAdminPermissions);
        setLoadingPermissions(false);
        return;
      }

      const token = localStorage.getItem("token");
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          if (payload.role === "admin" || payload.isAdmin === true) {
            console.log("🔐 Admin user detected via token - bypassing all permission checks");
            setIsAdmin(true);
            setModulePermissions(fullAdminPermissions);
            setLoadingPermissions(false);
            return;
          }
        } catch (e) {
          console.log("Could not parse token for admin check, falling back to API");
        }
      }

      try {
        setLoadingPermissions(true);
        const workingCompanyFromStorage =
          profile?.workingCompany ||
          profile?.clientName ||
          localStorage.getItem("workingCompany") ||
          localStorage.getItem("clientName") ||
          "";

        if (!workingCompanyFromStorage) {
          console.warn("No workingCompany found — using default permissions");
          setModulePermissions({
            view: true,
            edit: true,
            detectCompanies: true,
            reset: true,
          });
          setLoadingPermissions(false);
          return;
        }

        const response = await apiFetch(
          `${BASE_URL}/api/admin-levels/resolve/permissions?workingCompany=${encodeURIComponent(workingCompanyFromStorage)}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (response.ok) {
          const json = await response.json();
          const permissions = json.data ?? json.permissions ?? json;

          console.log("🔍 [CompanyConfiguration] Raw permissions received:", permissions);

          if (
            permissions.role === "admin" ||
            permissions.isAdmin === true ||
            permissions.isSuperAdmin === true ||
            permissions.allModulesAllowed === true
          ) {
            console.log("🔐 Admin user detected via API - granting full access");
            setIsAdmin(true);
            setModulePermissions(fullAdminPermissions);
            setLoadingPermissions(false);
            return;
          }

          const normalize = (obj) => {
            const defaults = {
              view: true,
              edit: true,
              detectCompanies: true,
              reset: true,
            };

            if (!obj) return defaults;

            const modules = Array.isArray(obj.modules) ? obj.modules : [];
            const features = obj.features || {};

            const moduleCandidates = [
              "configurations", "configuration", "companyConfiguration",
              "company-configuration", "company_configuration",
              "CompanyConfiguration", "settings",
            ];

            let moduleKey = moduleCandidates.find((k) => modules.includes(k));
            if (!moduleKey && features && typeof features === "object") {
              moduleKey = Object.keys(features).find((k) => moduleCandidates.includes(k));
            }

            let viewEnabled = true;
            let editEnabled = false;
            let detectEnabled = false;
            let autoCreateEnabled = false;
            let manageDeptRulesEnabled = false;
            let resetEnabled = false;
            let viewCompanyInfo = true;
            let editCompanyInfo = false;
            let viewAttendance = true;
            let editAttendance = false;
            let viewOvertime = true;
            let editOvertime = false;
            let viewLeaves = true;
            let editLeaves = false;
            let viewAdvancements = true;
            let editAdvancements = false;

            if (moduleKey) {
              const entry = features[moduleKey];
              if (Array.isArray(entry)) {
                viewEnabled = entry.includes("view") || entry.includes("read") || entry.includes("viewConfigurations");
                editEnabled = entry.some((f) => f.includes("edit") || f.includes("update") || f.includes("save") || f.startsWith("edit"));
                detectEnabled = entry.includes("detectCompanies") || entry.includes("selectCompany");
                autoCreateEnabled = entry.includes("autoCreateConfigs");
                manageDeptRulesEnabled = entry.includes("manageDepartmentRules");
                resetEnabled = entry.includes("reset");
                viewCompanyInfo = viewEnabled;
                editCompanyInfo = entry.includes("editCompanyInfo");
                viewAttendance = viewEnabled;
                editAttendance = entry.some((f) => f === "editWorkSchedule" || f === "editLateRules" || f === "editAbsenceRules" || f === "editGracePeriods");
                viewOvertime = viewEnabled;
                editOvertime = entry.some((f) => f === "enableOvertime" || f === "editOvertimeRate" || f === "editOvertimeLimits" || f === "editOvertimeApproval");
                viewLeaves = viewEnabled;
                editLeaves = entry.some((f) => f === "editAnnualLeaveProgression" || f === "editSickLeaveQuota" || f === "editCasualLeaveQuota" || f === "editLeaveApprovalRules" || f === "editLeaveCarryover" || f === "leaveRules");
                viewAdvancements = viewEnabled;
                editAdvancements = entry.some((f) => f === "enableAdvancements" || f === "editAdvancementLimits" || f === "editAdvancementPercentage" || f === "editAdvancementApproval" || f === "editRepaymentTerms" || f === "advancementRules");
              } else if (entry && typeof entry === "object") {
                viewEnabled = Boolean(entry.view || entry.read || entry.viewConfigurations);
                editEnabled = Boolean(entry.edit || entry.update || entry.save || Object.keys(entry).some((k) => k.startsWith("edit")));
                detectEnabled = Boolean(entry.detectCompanies || entry.selectCompany);
                autoCreateEnabled = Boolean(entry.autoCreateConfigs);
                manageDeptRulesEnabled = Boolean(entry.manageDepartmentRules);
                resetEnabled = Boolean(entry.reset);
                viewCompanyInfo = viewEnabled;
                editCompanyInfo = Boolean(entry.editCompanyInfo);
                viewAttendance = viewEnabled;
                editAttendance = Boolean(entry.editWorkSchedule || entry.editLateRules || entry.editAbsenceRules || entry.editGracePeriods);
                viewOvertime = viewEnabled;
                editOvertime = Boolean(entry.enableOvertime || entry.editOvertimeRate || entry.editOvertimeLimits || entry.editOvertimeApproval);
                viewLeaves = viewEnabled;
                editLeaves = Boolean(entry.editAnnualLeaveProgression || entry.editSickLeaveQuota || entry.editCasualLeaveQuota || entry.editLeaveApprovalRules || entry.editLeaveCarryover || entry.leaveRules);
                viewAdvancements = viewEnabled;
                editAdvancements = Boolean(entry.enableAdvancements || entry.editAdvancementLimits || entry.editAdvancementPercentage || entry.editAdvancementApproval || entry.editRepaymentTerms || entry.advancementRules);
              }
            } else {
              viewEnabled = modules.length > 0;
            }

            return {
              view: viewEnabled,
              edit: editEnabled,
              detectCompanies: detectEnabled,
              autoCreateConfigs: autoCreateEnabled,
              manageDepartmentRules: manageDeptRulesEnabled,
              reset: resetEnabled,
              viewCompanyInfo,
              editCompanyInfo,
              viewAttendance,
              editAttendance,
              viewOvertime,
              editOvertime,
              viewLeaves,
              editLeaves,
              viewAdvancements,
              editAdvancements,
            };
          };

          setModulePermissions(normalize(permissions));
        } else {
          console.warn("Failed to fetch permissions, using defaults");
          setModulePermissions({ view: true, edit: true, detectCompanies: true, reset: true });
        }
      } catch (error) {
        console.error("Error fetching permissions:", error);
        setModulePermissions({ view: true, edit: true, detectCompanies: true, reset: true });
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, [BASE_URL]);

  const canEditField = (section, isEditingArchive) => {
    if (isAdmin) return true;
    if (isEditingArchive) return true;

    switch (section) {
      case "company":
        return modulePermissions.editCompanyInfo;
      case "attendance":
        return modulePermissions.editAttendance;
      case "overtime":
        return modulePermissions.editOvertime;
      case "leaves":
        return modulePermissions.editLeaves;
      case "advancements":
        return modulePermissions.editAdvancements;
      default:
        return modulePermissions.edit;
    }
  };

  return { isAdmin, modulePermissions, loadingPermissions, canEditField };
};

export default usePermissions;